"use client";

import React, { useEffect, useMemo, useState, useRef } from 'react';
import styles from './NavbarDropdown.module.css'; // Import specific styles for the Dropdown component

const DEFAULT_ITEMS = [
  { label: "Positions", href: "/Positions" },
  { label: "Moves 👟", href: "/Moves" },
  { label: "Simple Cycles", href: "/SimpleCycles" },
  { label: "Transitions", href: "/Transitions" },
  { label: "Combinations", href: "/Combinations" },
  { label: "Choreography", href: "/Choreography" },
  { label: "Exercises", href: "/Exercises" },
];

const Dropdown = ({ items = DEFAULT_ITEMS, label = "", cycle = true, className = "" }) => {
  const [isDropdownVisible, setDropdownVisible] = useState(false);
  const [animatedIndex, setAnimatedIndex] = useState(0);
  const dropdownRef = useRef(null); // Reference to the dropdown container
  const normalizedItems = useMemo(() => (
    (Array.isArray(items) ? items : DEFAULT_ITEMS).filter((item) => item?.label && item?.href)
  ), [items]);

  // Toggle dropdown on click
  const handleDropdownToggle = () => {
    setDropdownVisible(!isDropdownVisible);
  };

  // Close the dropdown when clicking outside of it
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setDropdownVisible(false);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, []);

  // Cycle the text every 2 seconds
  useEffect(() => {
    if (!cycle || normalizedItems.length < 2) return undefined;
    const timerId = setInterval(() => {
      setAnimatedIndex((prevIndex) => (prevIndex + 1) % normalizedItems.length);
    }, 2000);

    return () => clearInterval(timerId);
  }, [cycle, normalizedItems.length]);

  const visibleLabel = label || normalizedItems[animatedIndex]?.label || "Navigate";

  return (
    <div className={`${styles.dropdown} ${className}`} ref={dropdownRef}>
      {/* Trigger for dropdown - opens/closes on click */}
      <button
        type="button"
        className={styles.dropdownToggle}
        onClick={handleDropdownToggle}
        aria-haspopup="menu"
        aria-expanded={isDropdownVisible}
      >
        <div className={styles.cyclingTextContainer}>
          <span>{visibleLabel}</span>
        </div>
        <div className={styles.dropdownCaret}></div>
      </button>

      {/* Dropdown menu */}
      <ul
        className={`${styles.dropdownMenu} ${isDropdownVisible ? styles.showDropdown : styles.hideDropdown}`}
        role="menu"
      >
        {normalizedItems.map((item) => (
          <li key={item.href}>
            <a className={styles.dropdownItem} href={item.href} role="menuitem">
              {item.label}
            </a>
          </li>
        ))}
      </ul>
    </div>
  );
};

export default Dropdown;
